import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserPlus, Users } from "lucide-react";
import { AgencyRepForm } from "@/components/agency_reps/AgencyRepForm";
import { AgencyRepRow } from "@/components/agency_reps/AgencyRepRow";
import { MasterTeamPickerDialog } from "@/components/agency_reps/MasterTeamPickerDialog";

const empty = () => ({ first_name: "", last_name: "", title: "", email: "", phone: "" });

export default function AgencyRepsSection({ clientId }) {
  const [reps, setReps] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(empty());
  const [busy, setBusy] = useState(false);

  // Master-team picker state — separate dialog so the form below isn't
  // re-used. Only loaded when the dialog opens.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerList, setPickerList] = useState([]);
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [pickerBusy, setPickerBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/agency-reps`);
      setReps(data || []);
    } catch { setReps([]); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerLoading(true);
    setPickerSelected(new Set());
    try {
      // Refresh reps + master team in parallel so the filter sees current
      // state (and so a freshly-added rep is immediately reflected).
      const [{ data: repsData }, { data: teamData }] = await Promise.all([
        api.get(`/clients/${clientId}/agency-reps`),
        api.get("/team-members", { params: { active_only: true } }),
      ]);
      if (Array.isArray(repsData)) setReps(repsData);
      const linkedIds = new Set(
        (repsData || []).map((r) => r.team_member_id).filter(Boolean),
      );
      // Show everyone but tag the already-linked ones so the UX is honest
      // (better than silently hiding them — the user knows why they can't
      // re-add a familiar name).
      const merged = (teamData || []).map((m) => ({
        ...m, _already_linked: linkedIds.has(m.team_member_id),
      }));
      setPickerList(merged);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load master team");
      setPickerList([]);
    } finally { setPickerLoading(false); }
  };

  const togglePicked = (id) => {
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addFromMaster = async () => {
    if (pickerSelected.size === 0) {
      toast.error("Pick at least one team member"); return;
    }
    setPickerBusy(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/agency-reps/from-team-members`,
        { team_member_ids: Array.from(pickerSelected) },
      );
      toast.success(`Added ${data?.added ?? pickerSelected.size} team member(s)`);
      setPickerOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setPickerBusy(false); }
  };

  const cancel = () => { setAdding(false); setEditingId(null); setDraft(empty()); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        title: draft.title.trim() || null,
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
      };
      if (editingId) {
        await api.patch(`/clients/${clientId}/agency-reps/${editingId}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post(`/clients/${clientId}/agency-reps`, payload);
        toast.success("Team member added");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const beginEdit = (r) => {
    setAdding(false);
    setEditingId(r.rep_id);
    setDraft({
      first_name: r.first_name || "", last_name: r.last_name || "",
      title: r.title || "", email: r.email || "", phone: r.phone || "",
    });
  };

  const setPrimary = async (r) => {
    if (r.is_primary) return;
    try {
      await api.patch(`/clients/${clientId}/agency-reps/${r.rep_id}`, { is_primary: true });
      toast.success(`${r.name || r.first_name} is now the primary team contact`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set primary");
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/clients/${clientId}/agency-reps/${r.rep_id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const uploadAvatar = async (r, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/clients/${clientId}/agency-reps/${r.rep_id}/avatar`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    load();
  };

  const clearAvatar = async (r) => {
    await api.delete(`/clients/${clientId}/agency-reps/${r.rep_id}/avatar`);
    load();
  };

  return (
    <div className="space-y-4 pt-2" data-testid="agency-reps-section">
      <div className="flex items-end justify-between gap-3 flex-wrap pt-6 border-t border-zinc-200">
        <div>
          <div className="label-overline">Iceberg Digital Team</div>
          <h2 className="font-display font-extrabold text-2xl tracking-tight mt-1">
            Your team on this account
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            The Iceberg Digital people working on{" "}
            <strong>this client</strong>. They appear under the client contacts on the PDF
            report cover with their headshot, title, email and phone.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={openPicker}
            className="rounded-sm h-10"
            data-testid="agency-rep-pick-from-master-button">
            <Users className="h-4 w-4 mr-1.5" /> Add from master team
          </Button>
          <Button
            onClick={() => { setAdding(true); setEditingId(null); setDraft(empty()); }}
            className="rounded-sm bg-zinc-900 hover:bg-black text-white h-10"
            data-testid="agency-rep-add-button">
            <UserPlus className="h-4 w-4 mr-1.5" /> Add team member
          </Button>
        </div>
      </div>

      {(adding || editingId) && (
        <AgencyRepForm
          editingId={editingId}
          draft={draft}
          setDraft={setDraft}
          busy={busy}
          onSubmit={submit}
          onCancel={cancel}
        />
      )}

      <div className="space-y-2">
        {reps.map((r) => (
          <AgencyRepRow
            key={r.rep_id}
            r={r}
            clientId={clientId}
            onEdit={beginEdit}
            onSetPrimary={setPrimary}
            onRemove={remove}
            onUploadAvatar={uploadAvatar}
            onClearAvatar={clearAvatar}
          />
        ))}
        {reps.length === 0 && !adding && (
          <Card className="rounded-sm border-dashed border-zinc-300">
            <CardContent className="p-8 text-center text-sm text-zinc-500">
              No team members yet — click <strong>Add team member</strong> above to record
              who&apos;s working on this account (e.g. Marketing Director, Creative Manager).
            </CardContent>
          </Card>
        )}
      </div>

      <MasterTeamPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        loading={pickerLoading}
        list={pickerList}
        selected={pickerSelected}
        onToggle={togglePicked}
        busy={pickerBusy}
        onAdd={addFromMaster}
      />
    </div>
  );
}
